/**
 * ScoreGauge — displays a site health score as a circular SVG ring.
 *
 * When pagesScanned is 0 (or scoreValue is null), shows a clear "no data"
 * state instead of a misleading number.
 */

import type { ScoreBand, ScoreConfidence } from "../api/product-types.js";

interface ScoreGaugeProps {
  scoreValue: number | null;
  scoreBand: ScoreBand | null;
  confidence: ScoreConfidence | null;
  pagesScanned?: number;
  label?: string;
  /** Ring diameter in pixels. Default 160. */
  size?: number;
}

const BAND_LABELS: Record<ScoreBand, string> = {
  strong: "Strong",
  needs_improvement: "Needs improvement",
  accessibility_risk: "Accessibility risk",
  high_risk: "High risk",
};

const BAND_COLORS: Record<ScoreBand, string> = {
  strong: "#10b981",
  needs_improvement: "#f59e0b",
  accessibility_risk: "#f97316",
  high_risk: "#ef4444",
};

const CONFIDENCE_LABELS: Record<ScoreConfidence, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

export function ScoreGauge({
  scoreValue,
  scoreBand,
  confidence,
  pagesScanned,
  label = "Accessibility score",
  size = 160,
}: ScoreGaugeProps) {
  const noData =
    scoreValue == null || (pagesScanned != null && pagesScanned === 0);

  if (noData) {
    return (
      <div className="score-gauge score-gauge--no-data" aria-label={label}>
        <div className="score-gauge__ring">
          <GaugeRing size={size} percentage={0} color="#d1d5db" />
          <div className="score-gauge__center">
            <span className="score-gauge__value">—</span>
          </div>
        </div>
        <div className="score-gauge__meta">
          <div className="score-gauge__band">
            {pagesScanned === 0 ? "No pages could be scanned" : "No score yet"}
          </div>
          <div className="score-gauge__label">{label}</div>
        </div>
      </div>
    );
  }

  const rounded = Math.round(scoreValue);
  const color = scoreBand ? BAND_COLORS[scoreBand] : "#667eea";
  const bandLabel = scoreBand ? BAND_LABELS[scoreBand] : "No score yet";

  return (
    <div className="score-gauge" aria-label={`${label}: ${rounded} out of 100`}>
      <div className="score-gauge__ring">
        <GaugeRing size={size} percentage={rounded} color={color} />
        <div className="score-gauge__center" aria-live="polite">
          <span className="score-gauge__value">{rounded}</span>
          <span className="score-gauge__max">/100</span>
        </div>
      </div>
      <div className="score-gauge__meta">
        <div className="score-gauge__band" style={{ color }}>
          {bandLabel}
        </div>
        {confidence && (
          <div className="score-gauge__confidence">
            {CONFIDENCE_LABELS[confidence]}
          </div>
        )}
        <div className="score-gauge__label">{label}</div>
      </div>
    </div>
  );
}

/** SVG ring component */
function GaugeRing({
  size,
  percentage,
  color,
}: {
  size: number;
  percentage: number;
  color: string;
}) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="score-gauge__svg"
    >
      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      {/* Progress ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="score-gauge__progress"
      />
    </svg>
  );
}
